import { useState } from 'react';
import { readConnection, writeConnection } from '../api/client';
import { GameModeNames, ServerRegionNames } from '../domain/enums';
import { useConfig } from '../store/config';
import { Badge, Button, Card, Field, Select, TextInput } from '../ui';
import { useToast } from '../ui/toast';

export default function SettingsPage() {
  const { config, updateConfig } = useConfig();
  const toast = useToast();

  const stored = readConnection();
  const [baseUrl, setBaseUrl] = useState(stored?.baseUrl ?? '');
  const [apiKey, setApiKey] = useState(stored?.apiKey ?? '');

  const saveConnection = () => {
    const trimmedUrl = baseUrl.trim();
    const trimmedKey = apiKey.trim();

    if (!trimmedUrl && !trimmedKey) {
      writeConnection(null);
      toast.success('Using the built-in API path', 'Requests go through this site.');
      return;
    }
    if (!/^https?:\/\//i.test(trimmedUrl)) {
      toast.error('The API URL must start with http:// or https://');
      return;
    }
    if (!trimmedKey) {
      toast.error('An API key is required when targeting another host');
      return;
    }

    writeConnection({ baseUrl: trimmedUrl, apiKey: trimmedKey });
    toast.success('Connection saved', 'Requests now go to the host you entered.');
  };

  return (
    <div className="space-y-6">
      <Card
        title="Lobby defaults"
        description="Pre-filled whenever you open the create dialog."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Default lobby name">
            {({ id }) => (
              <TextInput
                id={id}
                value={config.lobbyNameDefault}
                onChange={(event) => updateConfig({ lobbyNameDefault: event.target.value })}
              />
            )}
          </Field>
          <Field label="Default league ID">
            {({ id }) => (
              <TextInput
                id={id}
                type="number"
                min={0}
                value={config.defaultLeagueId}
                onChange={(event) => updateConfig({ defaultLeagueId: Number(event.target.value) })}
              />
            )}
          </Field>
          <Field label="Default game mode">
            {({ id }) => (
              <Select
                id={id}
                value={config.defaultGameMode}
                onChange={(event) => updateConfig({ defaultGameMode: Number(event.target.value) })}
              >
                {Object.entries(GameModeNames).map(([value, name]) => (
                  <option key={value} value={value}>
                    {name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Default server region">
            {({ id }) => (
              <Select
                id={id}
                value={config.defaultServerRegion}
                onChange={(event) =>
                  updateConfig({ defaultServerRegion: Number(event.target.value) })
                }
              >
                {Object.entries(ServerRegionNames).map(([value, name]) => (
                  <option key={value} value={value}>
                    {name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <p className="mt-4 text-xs text-ink-faint">
          Saved in this browser only. Nothing is sent to the API.
        </p>
      </Card>

      <Card
        title="API connection"
        description="Leave empty when the site proxies the API for you."
        actions={
          stored ? <Badge tone="warn">custom host</Badge> : <Badge tone="ok">built-in proxy</Badge>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="API base URL" hint="e.g. https://lobby-api.example.com">
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                value={baseUrl}
                onChange={(event) => setBaseUrl(event.target.value)}
                placeholder="Leave empty to use this site"
              />
            )}
          </Field>
          <Field label="API key" hint="Stored in this browser and sent as X-API-Key">
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                type="password"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
                autoComplete="off"
              />
            )}
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="primary" onClick={saveConnection}>
            Save connection
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              writeConnection(null);
              setBaseUrl('');
              setApiKey('');
              toast.success('Cleared', 'Back to the built-in API path.');
            }}
          >
            Clear
          </Button>
        </div>

        <p className="mt-4 text-xs text-ink-faint">
          In the Docker deployment the server holds the key and adds it to every request, so this
          section only matters when pointing a local client at a remote API.
        </p>
      </Card>
    </div>
  );
}
